import { LoadingDarkIcon } from "@minervaproject/fds";
import clsx from "clsx";
import { debounce } from "lodash";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  BreakPointProvider,
  DefaultHelperButtons,
  DefaultSizeStyle,
  DefaultTextAlignStyle,
  Editor,
  STROKE_SIZES,
  TLComponents,
  TLPageId,
  TLUiActionsContextType,
  TLUiOverrides,
  TLUiToolsContextType,
  Tldraw,
  useValue,
} from "tldraw";

import {
  CustomNavigationPanel,
  CustomQuickActions,
  CustomToolbar,
  CustomZoomMenu,
} from "./custom_components";
import { useShareDbStore } from "./use_share_db_store";

import { ForumAPIContext } from "contexts/forum_api_context";
import { useCapabilities, useUserIdsWithCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import {
  PROFILE_PROPERTIES,
  useUserProperties,
  useUsersProperties,
} from "hooks/use_user_properties";
import { CapabilityType, ResourceType } from "shared/types";
import { classroomSelectors, localSelectors } from "store";
import { assignNewOwnerToResource } from "utils/resource";

import "tldraw/tldraw.css";
import "./tldraw_whiteboard.scss";

const WHITEBOARD_INITIALIZATION_TIMEOUT = 5000;
const RESIZE_THRESHOLD = 100;

// Override default stroke sizes
STROKE_SIZES.s = 1;
STROKE_SIZES.m = 2;
STROKE_SIZES.l = 3.5;
STROKE_SIZES.xl = 5;

DefaultTextAlignStyle.setDefaultValue("start");

const components: TLComponents = {
  ActionsMenu: () => null,
  DebugPanel: () => null,
  ErrorFallback: () => null,
  HelpMenu: () => null,
  MainMenu: () => null,
  NavigationPanel: () => null,
  PageMenu: () => null,
  SharePanel: CustomNavigationPanel,
  Toolbar: (props) => (
    <BreakPointProvider forceMobile>
      <CustomToolbar {...props} />
    </BreakPointProvider>
  ),
  TopPanel: DefaultHelperButtons,
  ZoomMenu: CustomZoomMenu,
};

const componentsWithCustomQuickActions: TLComponents = {
  ...components,
  QuickActions: CustomQuickActions,
};

function TldrawWhiteboard({
  resource,
  showControls = true,
  isOverlay = false,
  currentPage = 1,
  numberOfPages = 1,
  offsetLeft = 0,
  offsetTop = 0,
  zoom = 1,
}: {
  resource: ResourceType;
  showControls?: boolean;
  isOverlay?: boolean;
  currentPage?: number;
  numberOfPages?: number;
  offsetLeft?: number;
  offsetTop?: number;
  zoom?: number;
}) {
  const dispatch = useAppDispatch();
  const forumAPI = useContext(ForumAPIContext);
  const classId = useAppSelector(classroomSelectors.selectClassId());
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const user = useUserProperties(currentUserId, [
    "whiteboard_font_size_preference",
    "whiteboard_stroke_size_preference",
  ]);
  const { canControlClassroom, isVisibleInClass } = useCapabilities();
  const isPresentationView = useAppSelector(classroomSelectors.selectIsPresentationView());
  const studentsCanDraw = useAppSelector(classroomSelectors.selectStudentsCanDraw());
  const isReadOnly =
    !showControls || (!canControlClassroom && !studentsCanDraw) || !isVisibleInClass;
  const [hostUrl, setHostUrl] = useState("");
  const [tldrawEditor, setTldrawEditor] = useState<Editor | null>(null);
  const activeToolId = useValue(
    "activeToolId",
    () => tldrawEditor && tldrawEditor.getCurrentToolId(),
    [tldrawEditor],
  );
  const activeSizeStyle = useValue(
    "activeSizeStyle",
    () => tldrawEditor && tldrawEditor.getStyleForNextShape(DefaultSizeStyle),
    [tldrawEditor],
  );
  const [previousToolId, setPreviousToolId] = useState<string | null>(null);
  const [strokeSize, setStrokeSize] = useState<string | null>(
    user?.whiteboard_stroke_size_preference || "s",
  );
  const [fontSize, setFontSize] = useState<string | null>(
    user?.whiteboard_font_size_preference || "m",
  );
  const [defaultStrokeSize, setDefaultStrokeSize] = useState<string | undefined>(
    user?.whiteboard_stroke_size_preference,
  );
  const [defaultFontSize, setDefaultFontSize] = useState<string | undefined>(
    user?.whiteboard_font_size_preference,
  );
  const [hasDefaultStrokeSizeChanged, setHasDefaultStrokeSizeChanged] = useState(false);
  const [hasDefaultFontSizeChanged, setHasDefaultFontSizeChanged] = useState(false);
  const [containerObserver, setContainerObserver] = useState<ResizeObserver | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // The following is needed to assign a new resource owner if necessary
  const allUsers = Object.values(useUsersProperties(PROFILE_PROPERTIES.concat("is_present")));
  const canControlClassroomUserIds = useUserIdsWithCapability(CapabilityType.CAN_CONTROL_CLASSROOM);
  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );
  const breakoutRooms = Object.values(useAppSelector(classroomSelectors.selectBreakoutRooms()));

  const store = useShareDbStore({
    resourceId: resource.id,
    hostUrl,
    isOwner: resource.owner_id === currentUserId && !isPresentationView,
    reset: resource.reset_whiteboard || false,
  });

  const setCurrentPage = useMemo(() => {
    if (!tldrawEditor) {
      return () => {};
    }
    return debounce((pageId: TLPageId) => tldrawEditor.setCurrentPage(pageId), 100);
  }, [tldrawEditor]);

  const overrides: TLUiOverrides = {
    actions(_editor, actions): TLUiActionsContextType {
      const newActions = { ...actions };
      Object.keys(newActions).forEach((key) => {
        newActions[key].kbd = ""; // disable all keyboard shortcuts
      });
      return newActions;
    },
    tools(_editor, tools): TLUiToolsContextType {
      const newTools = { ...tools };
      Object.keys(newTools).forEach((key) => {
        newTools[key].kbd = ""; // disable all keyboard shortcuts
      });
      return newTools;
    },
  };

  useEffect(() => {
    async function getHostUrl() {
      const response = await forumAPI.getFlightWhiteboardsSignedUrl(resource.id, classId);
      setHostUrl(response.replace("https://", "wss://").replace("?id", "/socket/sharedb?id"));
    }
    getHostUrl();
  }, [classId, resource.id, forumAPI]);

  useEffect(() => {
    if (!hostUrl) return;
    let timeout: NodeJS.Timeout;
    if (store.status === "loading") {
      timeout = setTimeout(() => {
        if (store.status === "loading") {
          window.flightLogger.log("[TldrawWhiteboard] Whiteboard initialization timed out");
          assignNewOwnerToResource(
            resource,
            currentUserId,
            allUsers,
            dispatch,
            canControlClassroomUserIds,
            !!activeBreakoutSessionId,
            breakoutRooms,
          );
        }
      }, WHITEBOARD_INITIALIZATION_TIMEOUT);
    }
    return () => clearTimeout(timeout);
  }, [
    activeBreakoutSessionId,
    allUsers,
    breakoutRooms,
    canControlClassroomUserIds,
    currentUserId,
    dispatch,
    hostUrl,
    resource,
    store.status,
  ]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (tldrawEditor && isOverlay) {
      const pageId = tldrawEditor
        .getPages()
        .find((page) => page.name === `Page ${currentPage}`)?.id;
      if (pageId && tldrawEditor.getCurrentPageId() !== pageId) {
        window.flightLogger.log(`[TldrawWhiteboard] set current page to ${currentPage}`);
        setCurrentPage(pageId);
      }
      // If offsets are provided, set the camera to the correct position
      // For PDFs, we align the camera to the top left corner of the page
      // HACK: the timeout prevents "cannot change atoms during reaction cycle" errors
      // and also prevents the camera from being misaligned in overlay mode
      timeout = setTimeout(() => {
        window.flightLogger.log(
          `[TldrawWhiteboard] set camera offsetLeft=${offsetLeft} offsetTop=${offsetTop} zoom=${zoom}`,
        );
        tldrawEditor.setCamera({ x: offsetLeft / zoom, y: offsetTop / zoom, z: zoom });
      }, 100);
    }

    return () => clearTimeout(timeout);
  }, [currentPage, isOverlay, offsetLeft, offsetTop, setCurrentPage, tldrawEditor, zoom]);

  useEffect(() => {
    if (!tldrawEditor || !tldrawEditor.getInstanceState()) return;

    if (tldrawEditor.getInstanceState().isReadonly !== isReadOnly) {
      tldrawEditor.updateInstanceState({ isReadonly: isReadOnly });
    }

    if (!isReadOnly) {
      tldrawEditor.setCurrentTool("draw");
    }
  }, [isReadOnly, tldrawEditor]);

  const setZoomLevelForNonOverlayWhiteboard = (editor: Editor) => {
    if (editor.getCurrentPageShapeIds().size > 0) {
      // If there are already shapes on the whiteboard, zoom to fit
      editor.zoomToFit();
      // But we don't want zoom to be greater than 100%
      if (editor.getZoomLevel() > 1) {
        editor.resetZoom();
      }
    } else {
      // Otherwise, determine the initial zoom level based on the size of the container
      const container = containerRef.current;
      if (!container) return;

      // Assume that the baseline is a 1-up stage layout on a 15" MacBook Pro.
      // In this case, the container is 1400x745, which will have the default zoom level of 1.0.
      // For containers smaller than this, we'll scale the zoom linearly.
      // For containers larger than this, we'll keep the zoom at 1.0.
      if (container.clientWidth < 1400) {
        const zoomLevel = Math.round((container.clientWidth / 1400) * 10) / 10;
        window.flightLogger.log(
          `[TldrawWhiteboard] set zoom level for blank whiteboard to ${zoomLevel}`,
        );
        editor.setCamera({ x: 0, y: 0, z: zoomLevel });
      } else {
        editor.resetZoom();
      }
    }
  };

  const handleOnMount = (editor: Editor) => {
    setTldrawEditor(editor);

    // Create one whiteboard page per PDF page/HTML slide
    if (editor.getPages().length < numberOfPages) {
      for (let i = editor.getPages().length; i < numberOfPages; i += 1) {
        editor.createPage({ name: `Page ${i + 1}` });
      }
    }

    // Disable edge scrolling
    editor.user.updateUserPreferences({ edgeScrollSpeed: 0 });

    // Set read-only
    editor.updateInstanceState({ isReadonly: isReadOnly });

    if (isOverlay) {
      // After the whiteboard initially loads, set the camera
      window.flightLogger.log(
        `[TldrawWhiteboard] on mount set camera offsetLeft=${offsetLeft} offsetTop=${offsetTop} zoom=${zoom}`,
      );
      editor.setCamera({ x: offsetLeft / zoom, y: offsetTop / zoom, z: zoom });
    } else {
      let prevWidth = 0;
      let prevHeight = 0;

      const resizeObserverCallback = debounce((entries) => {
        for (const entry of entries) {
          const width = entry.borderBoxSize?.[0].inlineSize;
          const height = entry.borderBoxSize?.[0].blockSize;
          if (
            (prevWidth === 0 && prevHeight === 0) || // set zoom level for initial render
            Math.abs(prevWidth - width) > RESIZE_THRESHOLD || // or if the container size changes by more than 100px in either dimension
            Math.abs(prevHeight - height) > RESIZE_THRESHOLD
          ) {
            setZoomLevelForNonOverlayWhiteboard(editor);
          }
          prevWidth = width;
          prevHeight = height;
        }
      }, 300);

      const _containerObserver = new ResizeObserver(resizeObserverCallback);
      if (containerRef.current) {
        _containerObserver.observe(containerRef.current);
        setContainerObserver(_containerObserver);
      }
    }
  };

  useEffect(() => {
    const current = containerRef.current;

    return () => {
      if (containerObserver && current) {
        containerObserver.unobserve(current);
      }
    };
  }, [containerObserver]);

  useEffect(() => {
    if (!tldrawEditor) {
      return;
    }

    // Tell tldraw to update the stroke size/font size for the next shapes when the preferences for this whiteboard
    // changes, or when the default preference changes
    if ((activeToolId === "draw" && previousToolId !== "draw") || hasDefaultStrokeSizeChanged) {
      tldrawEditor.setStyleForNextShapes(DefaultSizeStyle, strokeSize);
      setHasDefaultStrokeSizeChanged(false);
    } else if (
      (activeToolId === "text" && previousToolId !== "text") ||
      hasDefaultFontSizeChanged
    ) {
      // Lock text tool so that switching text styles is more intuitive
      tldrawEditor.updateInstanceState({ isToolLocked: true });
      tldrawEditor.setStyleForNextShapes(DefaultSizeStyle, fontSize);
      setHasDefaultFontSizeChanged(false);
    }

    setPreviousToolId(activeToolId);
  }, [
    activeToolId,
    fontSize,
    hasDefaultFontSizeChanged,
    hasDefaultStrokeSizeChanged,
    previousToolId,
    strokeSize,
    tldrawEditor,
  ]);

  useEffect(() => {
    // Update the stroke size/font size preference for this whiteboard when activeSizeStyle changes
    if (activeToolId === "draw") {
      setStrokeSize(activeSizeStyle);
    } else if (activeToolId === "text") {
      setFontSize(activeSizeStyle);
    }
  }, [activeSizeStyle, activeToolId]);

  useEffect(() => {
    if (!tldrawEditor || !user) {
      return;
    }

    // Update the stroke size/font size preference for this whiteboard if the user updates their default preferences
    if (user.whiteboard_stroke_size_preference !== defaultStrokeSize) {
      setDefaultStrokeSize(user.whiteboard_stroke_size_preference);
      setStrokeSize(user.whiteboard_stroke_size_preference);
      setHasDefaultStrokeSizeChanged(true);
    }
    if (user.whiteboard_font_size_preference !== defaultFontSize) {
      setDefaultFontSize(user.whiteboard_font_size_preference);
      setFontSize(user.whiteboard_font_size_preference);
      setHasDefaultFontSizeChanged(true);
    }
  }, [defaultFontSize, defaultStrokeSize, tldrawEditor, user]);

  // TODO: handle error state
  return hostUrl && store.status === "synced-remote" ? (
    <div
      className={clsx("tldraw-container w-full h-full", {
        "tldraw-container--overlay": isOverlay,
        "tldraw-container--read-only": isReadOnly,
        "tldraw-container--hide-controls": isOverlay && !showControls,
      })}
      ref={containerRef}
    >
      <Tldraw
        store={store}
        autoFocus={false}
        onMount={handleOnMount}
        components={canControlClassroom ? componentsWithCustomQuickActions : components}
        overrides={overrides}
      />
    </div>
  ) : (
    <LoadingDarkIcon className="absolute-center" data-testid="loading" />
  );
}

export { TldrawWhiteboard };
